import React, { useState } from "react";
import {
  YStack,
  XStack,
  Text,
  Image,
  SizableText,
  H3,
  H4,
  Card,
  Separator,
  Button,
  Spinner
} from "tamagui";
import { useQuery } from "@tanstack/react-query";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import * as Animatable from 'react-native-animatable';
import { View, Linking, Dimensions, FlatList, Pressable, StyleSheet } from "react-native";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";
import { Image as ExpoImage } from 'expo-image';

type TabType = 'profile' | 'posts';

const InstagramOptions = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["instagramProfile"],
    queryFn: async () => await apis.getInstagramProfile(),
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const { data: mediaData, isLoading: isLoadingMedia, error: mediaError, refetch: refetchMedia } = useQuery({
    queryKey: ["instagramMedia"],
    queryFn: async () => await apis.getInstagramMedia(25),
    enabled: activeTab === 'posts' && !!data,
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const colors = {
    background: theme ? '#ffffff' : '#1e293b',
    cardBackground: theme ? '#f8fafc' : '#334155',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: '#E1306C',
  };

  // Safely get profile data - handle both wrapped and unwrapped responses
  const profile = data?.profile || data || null;

  // Ensure profile has all expected fields with defaults
  const profileData = profile ? {
    id: profile.id,
    username: profile.username || 'username',
    accountType: profile.accountType || profile.account_type,
    mediaCount: profile.mediaCount || profile.media_count || 0,
    followersCount: profile.followersCount || profile.followers_count || 0,
    followsCount: profile.followsCount || profile.follows_count || 0,
    profilePictureUrl: profile.profilePictureUrl || profile.profile_picture_url || null,
    fullName: profile.fullName || profile.full_name || profile.name || profile.username || 'Instagram User',
  } : null;

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const renderMediaItem = ({ item, index }: { item: any; index: number }) => {
    const isVideo = item.media_type === 'VIDEO' || item.media_type === 'CAROUSEL_VIDEO';
    const thumbnailUrl = item.thumbnail_url || item.media_url;
    const mediaUrl = item.media_url;

    return (
      <Animatable.View
        animation="fadeInUp"
        duration={400}
        delay={index * 50}
        style={{ flex: 1, margin: 4 }}
      >
        <Pressable
          onPress={() => {
            if (mediaUrl) {
              Linking.openURL(mediaUrl).catch(err => console.error('Error opening media:', err));
            }
          }}
          style={({ pressed }) => [
            {
              aspectRatio: 1,
              borderRadius: 12,
              overflow: 'hidden',
              backgroundColor: colors.cardBackground,
              opacity: pressed ? 0.8 : 1,
            }
          ]}
        >
          <ExpoImage
            source={{ uri: thumbnailUrl || mediaUrl }}
            contentFit="cover"
            style={{ width: '100%', height: '100%' }}
            transition={200}
          />
          {isVideo && (
            <View style={styles.videoBadge}>
              <MaterialCommunityIcons name="play-circle" size={20} color="white" />
            </View>
          )}
          {item.media_type === 'CAROUSEL_ALBUM' && (
            <View style={styles.carouselBadge}>
              <MaterialCommunityIcons name="image-multiple" size={16} color="white" />
            </View>
          )}
        </Pressable>
        {item.caption && (
          <SizableText
            size="$2"
            color={colors.textSecondary}
            numberOfLines={2}
            marginTop="$1"
            paddingHorizontal="$1"
          >
            {item.caption}
          </SizableText>
        )}
        {item.timestamp && (
          <SizableText
            size="$1"
            color={colors.textSecondary}
            marginTop="$0.5"
            paddingHorizontal="$1"
          >
            {formatDate(item.timestamp)}
          </SizableText>
        )}
      </Animatable.View>
    );
  };

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Tab Navigation */}
        {profileData && (
          <Animatable.View animation="fadeInDown" duration={400}>
            <Card
              backgroundColor={colors.cardBackground}
              borderRadius={12}
              padding="$2"
              elevate
            >
              <XStack gap="$2">
                <Button
                  flex={1}
                  backgroundColor={activeTab === 'profile' ? colors.accent : 'transparent'}
                  color={activeTab === 'profile' ? 'white' : colors.text}
                  onPress={() => setActiveTab('profile')}
                  borderRadius={8}
                  paddingVertical="$2"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialCommunityIcons
                      name="account-circle"
                      size={18}
                      color={activeTab === 'profile' ? 'white' : colors.text}
                    />
                    <SizableText fontWeight="600" size="$3">
                      Profile
                    </SizableText>
                  </XStack>
                </Button>
                <Button
                  flex={1}
                  backgroundColor={activeTab === 'posts' ? colors.accent : 'transparent'}
                  color={activeTab === 'posts' ? 'white' : colors.text}
                  onPress={() => setActiveTab('posts')}
                  borderRadius={8}
                  paddingVertical="$2"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialCommunityIcons
                      name="image-multiple"
                      size={18}
                      color={activeTab === 'posts' ? 'white' : colors.text}
                    />
                    <SizableText fontWeight="600" size="$3">
                      Posts
                    </SizableText>
                  </XStack>
                </Button>
              </XStack>
            </Card>
          </Animatable.View>
        )}

        {/* Error State */}
        {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3
                  color="#ef4444"
                  fontWeight="600"
                  textAlign="center"
                >
                  Failed to load Instagram profile
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  Please try reconnecting your Instagram account, or try removing and re-adding the integration.  
                </SizableText>
                <Button
                  backgroundColor="#ef4444"
                  color="white"
                  onPress={() => refetch()}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Retry Connection
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#E1306C" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading Instagram profile...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {profileData && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header with Profile Info */}
                <XStack alignItems="center" gap="$4" marginBottom="$4">
                  <View style={{
                    width: 70,
                    height: 70,
                    borderRadius: 35,
                    backgroundColor: '#E1306C',
                    padding: 3,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {profileData.profilePictureUrl ? (
                      <Image
                        source={{ uri: profileData.profilePictureUrl }}
                        width={64}
                        height={64}
                        borderRadius={32}
                      />
                    ) : (
                      <MaterialCommunityIcons name="instagram" size={40} color="white" />
                    )}
                  </View>

                  <YStack flex={1}>
                    <XStack alignItems="center" gap="$2">
                      <H4
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="700"
                        numberOfLines={1}
                      >
                        {profileData.fullName}
                      </H4>
                      {profileData.accountType === 'BUSINESS' && (
                        <MaterialCommunityIcons
                          name="check-decagram"
                          size={20}
                          color="#E1306C"
                        />
                      )}
                    </XStack>
                    <SizableText
                      size="$4"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={1}
                    >
                      @{profileData.username}
                    </SizableText>
                  </YStack>
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Statistics */}
                <XStack gap="$3" justifyContent="space-around" flexWrap="wrap">
                  <Stat
                    icon="account-group"
                    label="Followers"
                    value={profileData.followersCount}
                    theme={theme}
                  />
                  <Stat
                    icon="account-plus"
                    label="Following"
                    value={profileData.followsCount}
                    theme={theme}
                  />
                  <Stat
                    icon="image-multiple"
                    label="Posts"
                    value={profileData.mediaCount}
                    theme={theme}
                  />
                </XStack>

                {/* Action Button */}
                <Button
                  backgroundColor="#E1306C"
                  color="white"
                  marginTop="$4"
                  onPress={async () => {
                    if (profileData.username) {
                      const url = `https://instagram.com/${profileData.username}`;
                      try {
                        await Linking.openURL(url);
                      } catch (error) {
                        console.error("Error opening Instagram profile:", error);
                      }
                    }
                  }}
                  borderRadius={12}
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="open-in-new" size={18} color="white" />
                    <SizableText color="white" fontWeight="600" size="$4">
                      View Profile on Instagram
                    </SizableText>
                  </XStack>
                </Button>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            {activeTab === 'profile' && (
              <Animatable.View animation="fadeInUp" duration={600} delay={200}>
                <Card
                  backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                  borderColor={theme ? "#dcfce7" : "#065f46"}
                  borderRadius={16}
                  padding="$5"
                  elevate
                >
                  <YStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#d1fae5" : "#065f46",
                      borderRadius: 50,
                      padding: 12
                    }}>
                      <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                    </View>
                    <H4
                      color={theme ? "#059669" : "#10b981"}
                      fontWeight="600"
                      textAlign="center"
                    >
                      Instagram Connected Successfully!
                    </H4>
                    <SizableText
                      size="$4"
                      color={theme ? "#065f46" : "#34d399"}
                      textAlign="center"
                    >
                      You can now access your Instagram profile and media directly from SmartSaaS.
                    </SizableText>
                  </YStack>
                </Card>
              </Animatable.View>
            )}
          </>
        )}

        {/* Posts/Media Tab Content */}
        {activeTab === 'posts' && profileData && (
          <>
            {/* Media Loading State */}
            {isLoadingMedia && (
              <Animatable.View animation="fadeIn" duration={400}>
                <Card
                  backgroundColor={colors.cardBackground}
                  borderRadius={16}
                  padding="$6"
                  elevate
                >
                  <YStack alignItems="center" gap="$4">
                    <Spinner size="large" color={colors.accent} />
                    <H4 color={colors.text} fontWeight="600">
                      Loading your posts...
                    </H4>
                  </YStack>
                </Card>
              </Animatable.View>
            )}

            {/* Media Error State */}
            {mediaError && (
              <Animatable.View animation="fadeInUp" duration={400}>
                <Card
                  backgroundColor={theme ? "#fef2f2" : "#450a0a"}
                  borderColor={theme ? "#fecaca" : "#7f1d1d"}
                  borderRadius={16}
                  padding="$5"
                  elevate
                >
                  <YStack alignItems="center" gap="$4">
                    <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                    <H4 color="#ef4444" fontWeight="600" textAlign="center">
                      Failed to load posts
                    </H4>
                    <SizableText
                      size="$4"
                      color={theme ? "#64748b" : "#94a3b8"}
                      textAlign="center"
                    >
                      {mediaError?.message || 'Unable to fetch Instagram posts. Please try again.'}
                    </SizableText>
                    <Button
                      backgroundColor="#ef4444"
                      color="white"
                      onPress={() => refetchMedia()}
                      borderRadius={12}
                      paddingHorizontal="$5"
                      paddingVertical="$3"
                    >
                      <XStack alignItems="center" gap="$2">
                        <MaterialIcons name="refresh" size={20} color="white" />
                        <SizableText color="white" fontWeight="600">
                          Retry
                        </SizableText>
                      </XStack>
                    </Button>
                  </YStack>
                </Card>
              </Animatable.View>
            )}

            {/* Media Grid */}
            {mediaData?.data && mediaData.data.length > 0 && (
              <Animatable.View animation="fadeInUp" duration={600}>
                <Card
                  backgroundColor={colors.background}
                  borderRadius={16}
                  padding="$4"
                  elevate
                >
                  <YStack gap="$4">
                    <XStack alignItems="center" justifyContent="space-between">
                      <H4 color={colors.text} fontWeight="600">
                        Your Posts ({mediaData.data.length})
                      </H4>
                      <Button
                        backgroundColor="transparent"
                        color={colors.textSecondary}
                        onPress={() => refetchMedia()}
                        paddingHorizontal="$2"
                        paddingVertical="$1"
                      >
                        <MaterialIcons name="refresh" size={18} color={colors.textSecondary} />
                      </Button>
                    </XStack>
                    <FlatList
                      data={mediaData.data}
                      renderItem={renderMediaItem}
                      numColumns={isTablet ? 4 : 3}
                      keyExtractor={(item, index) => item.id || `media-${index}`}
                      scrollEnabled={false}
                      contentContainerStyle={{ paddingBottom: 8 }}
                      columnWrapperStyle={isTablet || mediaData.data.length > 2 ? { gap: 8 } : undefined}
                    />
                  </YStack>
                </Card>
              </Animatable.View>
            )}

            {/* Empty State */}
            {!isLoadingMedia && !mediaError && (!mediaData?.data || mediaData.data.length === 0) && (
              <Animatable.View animation="fadeInUp" duration={400}>
                <Card
                  backgroundColor={colors.cardBackground}
                  borderRadius={16}
                  padding="$6"
                  elevate
                >
                  <YStack alignItems="center" gap="$4">
                    <View style={{
                      backgroundColor: theme ? "#fdf2f8" : "#831843",
                      borderRadius: 50,
                      padding: 16
                    }}>
                      <MaterialCommunityIcons name="image-off" size={48} color={colors.accent} />
                    </View>
                    <H4 color={colors.text} fontWeight="600" textAlign="center">
                      No posts found
                    </H4>
                    <SizableText
                      size="$4"
                      color={colors.textSecondary}
                      textAlign="center"
                    >
                      You don't have any posts yet, or they couldn't be retrieved.
                    </SizableText>
                  </YStack>
                </Card>
              </Animatable.View>
            )}
          </>
        )}
      </YStack>
    </ErrorBoundary>
  );
};

const Stat = ({ icon, label, value, theme }: { icon: string; label: string; value: number; theme: boolean }) => (
  <YStack alignItems="center" gap="$2" minWidth={80}>
    <View style={{
      backgroundColor: theme ? "#fdf2f8" : "#831843",
      borderRadius: 12,
      padding: 10
    }}>
      <MaterialCommunityIcons name={icon as any} size={24} color="#E1306C" />
    </View>
    <SizableText
      fontSize="$6"
      fontWeight="700"
      color={theme ? "#1e293b" : "#f1f5f9"}
    >
      {value.toLocaleString()}
    </SizableText>
    <SizableText
      fontSize="$3"
      color={theme ? "#64748b" : "#94a3b8"}
    >
      {label}
    </SizableText>
  </YStack>
);

const styles = StyleSheet.create({
  videoBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 20,
    padding: 4,
  },
  carouselBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 20,
    padding: 4,
  },
});

export default InstagramOptions;